// src/encounter/encounter-drawer.tsx
// RIGHT column of the 3-column encounter shell (Flutter sidePanel(), encounter_screen.dart:1655).
//
// Showcase look (Antonin, 15 Oct: "right panel of the encounter is unusable. make it clean
// collapsible sections. Check proto for inspiration"). It was a 4-tab strip (Timeline · Note ·
// Orders · Edit) that showed one panel at a time, a Timeline that could only ever say "will appear
// here once the feed is available" (the page never fed it), and an Edit tab that runs a check
// engine. It is now ONE scrolling column of collapsible sections (`panel-section.tsx`), in this
// order, short ones first so the long note never pushes them out of sight:
//   1. Scrips Intelligence — the ambient scribe (PROD-1944), when the page mounts it. Open.
//   2. Orders — the A/P orders summary (PROD-1263). Open when there are orders.
//   3. Clinical edits — ClinicalEditTab on RunClinicalEdits (PROD-1262). Closed: it runs only when
//      opened, as the tab did.
//   4. Care pathway — the SAME /history feed the pre-encounter column reads (PROD-738), through the
//      same `CarePathwayFeed`. Open when the pathway has activities.
//   5. Note — the generated note, its attestation and its footer (Sign Note stays the one primary
//      action). Open.
// No tab is a separate mode: each one held a read-only panel, so each became a section. The same
// sections are the pre-encounter column's (`care-pathway-rail.tsx`), so the two states read alike.
import type { ReactNode } from 'react';
import {
  ContextRail,
  OrdersSummary,
  type OrdersSummaryGroups,
  type GeneratedNotePatient,
} from '@scripsteam/scrips-signal-ds';
import { ClinicalEditTab } from './clinical-edit-tab';
import { CarePathwayFeed } from './care-pathway-rail';
import { CarePathwayTimeline } from './care-pathway-timeline';
import type { CarePathwayActivity } from './lib/care-pathway';
import { isOrdersSummaryEmpty } from './lib/to-orders-summary';
import { PanelSection } from './panel-section';
import { env } from '@/config/env';
import { IntelligenceCards } from './intelligence/intelligence-cards';
import type { NoteSection } from './generated-note';
import { GeneratedNoteBody } from './generated-note-body';
// Re-exported (PROD-1999): the A/P note types moved next to the component that renders them, and
// `to-ap-note.ts` plus existing callers import them from here.
export type { ApNoteRow, ApNoteSection, NoteDocRow } from './generated-note-body';
export type { NoteSectionKey } from './generated-note-body';
import type { ApNoteSection } from './generated-note-body';
import type { QuestionnaireStep } from './lib/questionnaire-groups';
import type { NoteDocRow } from './generated-note-body';
import type { NoteComplaints } from './note-column/to-note-complaints';
// Lives in its own module so the PAGE can import it without going through this file —
// two page suites mock './encounter-drawer' and must not have to know about it.
import { SignedAttestation } from './note-column/signed-attestation';
export { SignedAttestation } from './note-column/signed-attestation';
import type { NoteSectionKey } from './generated-note-body';

/** Orders in the summary, for the Orders section's count. */
function orderCount(groups: OrdersSummaryGroups | undefined): number {
  if (!groups) return 0;
  return Object.values(groups).reduce((n, g) => n + (g?.length ?? 0), 0);
}

/**
 * One coded row of an A/P note section: a code column + a name/sub pair.
 * `code` is the JUSTIFYING diagnosis code (ICD-10) on an order row, and the diagnosis's own
 * code on a Diagnosis row. `sub` is the row's secondary line — the CPT/procedure code on a
 * lab/procedure, the sig on a medication. Both optional: a row with no code renders an empty
 * gutter rather than a fabricated one.
 */

/**
 * A grouped section of the A/P generated note (design lines 820-846): `Diagnosis` first,
 * then the order groups (`Labs` · `Medications` · `Vaccination` · `Procedure`).
 *
 * `kind` carries the design's two row treatments, not a colour:
 *   - `'diagnosis'` → UPPERCASE heading, rows in `--color-text-secondary` (the note is
 *     restating the problem list, so the rows read as context);
 *   - `'order'` (default) → title-case heading, rows in `--color-brand-primary` (the
 *     actionable things this encounter placed).
 */

export interface EncounterDrawerProps {
  /**
   * Ambient scribe panel (PROD-1944), drawn as the first section, "Scrips Intelligence".
   *
   * A `ReactNode` rather than typed props, following the `noteFooter` seam: the page owns the
   * session lifecycle and every handler, and the drawer just places the node. Optional on
   * purpose — omit it and there is no Intelligence section.
   */
  ambient?: ReactNode;
  /**
   * The patient the Scrips Intelligence "Explain a body system" entry reads for (POST /api/intelligence/explain with
   * the clinician's own token). Drawn below the ambient scribe, inside the same section, only when the
   * VITE_INTELLIGENCE_ENGINE flag is on (dev bundle only). Absent or flag off: the section is exactly as before.
   * When absent, the drawer uses `docsPatientId`, the open encounter's patient the page already passes.
   */
  intelligencePatientId?: string | null;
  /** Generated-note sections (from responseToNote). Empty until the encounter
   *  questionnaires are answered — the Note section then shows the live note. */
  noteSections?: NoteSection[];
  /**
   * RETIRED (2026-08-21, PROD-2011): this drove the per-section "Go To <section>" deep link from
   * PROD-1588 I3. Flutter has no such link on a populated note section, so the link is gone and
   * with it this prop. Sections still carry their step identity for grouping and scroll-follow.
   */
  onGoToStep?: (step: QuestionnaireStep) => void;
  /** Encounter id — enables the Clinical edits section's RunClinicalEdits fetch. */
  encounterId?: string;
  /** Grouped A/P orders for the Orders section (PROD-1263). Read-only summary; empty/absent
   *  → one line. Sign-and-send / per-category print are deferred (write/print path). */
  orders?: OrdersSummaryGroups;
  /**
   * D4 NOTE FOOTER (Samer decision strip 2026-07-22, PROD-1588): the note's terminal
   * action row (DS NoteFooter — Sign & Lock / View PVS / Discard This Note) mounted at
   * the BOTTOM of the Note section, the frame's footer position (E1 613:2964). The page
   * composes the DS molecule with its live handlers (sign-lock, PVS print) so the
   * drawer stays presentational. Absent → no footer (Storybook/no-data paths).
   */
  noteFooter?: ReactNode;
  /**
   * The encounter IS signed. Distinct from `signedAt`, which is only the TIMESTAMP: Flutter shows
   * its "Signed" pill on the signed state and leaves the date line empty when the record has no
   * `createdDate` (generated_notes_widget.dart:376-400).
   */
  signed?: boolean;
  /**
   * Care-pathway activities for the Care pathway section (PROD-1261), supplied by the host. It
   * WINS over `appointmentId` and skips the read (stories and tests drive the states with it);
   * the page passes `appointmentId` instead.
   */
  activities?: CarePathwayActivity[];
  /**
   * The appointment whose care pathway the Care pathway section reads (GET
   * workflows/appointments/{id}/history, through `CarePathwayFeed`, the pre-encounter column's read
   * and query key, so it is usually a cache hit). `activities` wins over it when both are given.
   */
  appointmentId?: string;
  /**
   * Patient identity stamp at the TOP of the note (design lines 797-813 — name · MRN · DoB,
   * then visit type · date, then "Created by <avatar> <name>"). Passed straight through to the
   * DS `GeneratedNote.patient` seam — the block is NOT hand-drawn here.
   *
   * Fed from the encounter's own patient + appointment data (the same `patient` /
   * `encounterQuery.data` the header banner already reads). ANTI-FICTION: every sub-field is
   * optional and the organism omits what it is not given — leave a field `undefined` when the
   * wire has no value for it (notably `createdBy`, whose author name is not on the encounter
   * payload today). Never substitute a placeholder identity on a clinical note.
   */
  patient?: GeneratedNotePatient;
  /**
   * "REASON FOR VISIT" eyebrow + value (design lines 815-818). Passed through to the DS
   * `GeneratedNote.reasonForVisit` seam. Source: `encounter.reasonForVisitName` (already read
   * by the page for the banner). Absent → the DS renders no block.
   */
  reasonForVisit?: string;
  /**
   * A/P note sections — the grouped `Diagnosis` + order rows the design shows in place of the
   * derived questionnaire note while the A/P step is active (lines 820-846). The DRAWER holds
   * no step state: the page passes this only on the A/P step, and its presence swaps the note
   * body. Absent → the normal `noteSections` note.
   *
   * Deliberately UNFED at this commit — the mapping from the live `PatientAssessment`
   * (diagnoses + per-type orders) belongs in the page next to the assessment query. No
   * fixture is wired in as a stand-in.
   */
  apNoteSections?: ApNoteSection[];
  /**
   * The step the clinician is on, for the note's scroll-follow (PROD-1999 follow-up). Forwarded
   * to `GeneratedNoteBody`; see there for Flutter's behaviour and the two cases it skips.
   */
  activeStep?: string;
  /**
   * When the note was signed, as the backend's ISO timestamp (design lines 836-844: green
   * check + "Signed" + the timestamp). Renders the ATTESTATION line only — it is not an
   * action, and it does not replace `noteFooter` (Sign & Lock / View PVS stay).
   *
   * Real source: `signLockSignedAt(signLockRecords)` in `lib/to-sign-lock.ts`, which returns
   * the `createdDate` of the lock-or-sign record that produced the current state, and `null`
   * both for a draft encounter AND for a signed one whose record has no date. Absent/unparseable
   * → no attestation (and never a fabricated "now").
   */
  signedAt?: string;
  /**
   * The encounter's documents, as the note's DOCS section (`generated_notes_widget.dart:192`).
   * Forwarded to `GeneratedNoteBody`; see there for why an absent value omits the section while
   * an empty array renders its empty state.
   */
  docs?: NoteDocRow[];
  /**
   * Instead of `docs`, the ids to READ the documents with. Forwarded to `GeneratedNoteBody`, which
   * mounts the query only when BOTH are present — so this drawer's own suite, which passes
   * neither, still needs no `QueryClientProvider`.
   */
  docsPatientId?: string | null;
  docsEncounterId?: string | null;
  /** The saved chief complaints — forwarded to `GeneratedNoteBody`. */
  chiefComplaints?: NoteComplaints;
  /**
   * `enableAction` (`notes_component_widget.dart:130`) — gates the empty-state call to action.
   * Default false; the in-encounter host leaves it false because Flutter's own gate (the
   * appointment being startable) is no longer true once the encounter is running.
   */
  canStartEncounter?: boolean;
  /** `isAppointmentStarted` (`:132`) — drops the "Start Appointment and " prefix from the CTA. */
  isAppointmentStarted?: boolean;
  /** Fired by the empty-state CTA with the section it names. */
  onGoToSection?: (key: NoteSectionKey) => void;
  /**
   * The rail's width, passed straight to the DS `ContextRail`. `'fill'` = the flex remainder of
   * the row (Flutter's `Expanded` note panel, encounter_screen.dart:1025) — the started encounter
   * passes it. Default 416, the resting width every other host and story renders at.
   */
  width?: number | 'fill';
}

export function EncounterDrawer({
  ambient,
  intelligencePatientId,
  noteSections,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onGoToStep: _onGoToStep,
  encounterId,
  orders,
  noteFooter,
  signed,
  activities,
  appointmentId,
  docs,
  docsPatientId,
  docsEncounterId,
  chiefComplaints,
  canStartEncounter,
  isAppointmentStarted,
  onGoToSection,
  patient,
  reasonForVisit,
  apNoteSections,
  activeStep,
  signedAt,
  width = 416,
}: EncounterDrawerProps = {}) {
  const hasOrders = !!orders && !isOrdersSummaryEmpty(orders);
  const explainPatient = intelligencePatientId ?? docsPatientId;
  const explainFor = env.intelligenceEngineEnabled && explainPatient ? explainPatient : null;

  return (
    // The drawer CHROME is the DS ContextRail frame (Figma "Note" rail instance 7624:451753: left
    // hairline + scrollable body). No header: with no tab strip there is nothing to put in it, and
    // each section carries its own title. Width 416 = Contract §4 right-drawer (§10 gate requires
    // ≥410px); the started encounter passes `width="fill"` (Flutter's `Expanded` note panel).
    <ContextRail ariaLabel="Encounter drawer" width={width} className="enc-right-panel">
      {ambient || explainFor ? (
        // PROD-1944: the ambient scribe, its own section (it sat above the note in the Note tab). Below it, behind the
        // engine flag, the read-only Explain entry (encounter owner ruling, 31 Oct: the drawer's existing section).
        <PanelSection id="intelligence" title="Scrips Intelligence" defaultOpen>
          {ambient}
          {/* DoH §3.4 gate + Explain + Next step, the same mount as frame v2's right column (intelligence-cards.tsx). */}
          {explainFor ? <IntelligenceCards patientId={explainFor} encounterId={encounterId ?? null} /> : null}
        </PanelSection>
      ) : null}
      <PanelSection id="orders" title="Orders" meta={String(orderCount(orders))} defaultOpen={hasOrders}>
        {hasOrders ? (
          <OrdersSummary groups={orders} />
        ) : (
          <p data-slot="panel-section-line">Orders placed in Assessment &amp; Plan appear here.</p>
        )}
      </PanelSection>
      <PanelSection id="clinical-edits" title="Clinical edits">
        {/* Mounted only while open, so RunClinicalEdits runs when the clinician asks, as the tab did. */}
        <ClinicalEditTab encounterId={encounterId} headless />
      </PanelSection>
      {/* Timeline (PROD-1261): the feed is real now (PROD-738); the four outcomes stay four lines. The
          read mounts only with an appointment to read for: with none there is nothing to ask, and the
          section says "isn't available yet" (or draws a host-supplied list) without a query client. */}
      {appointmentId ? (
        <CarePathwayFeed appointmentId={appointmentId} activities={activities} />
      ) : (
        <CarePathwayTimeline activities={activities} />
      )}
      <PanelSection id="note" title="Note" defaultOpen>
        {/* PROD-1999: the note body is the SHARED `GeneratedNoteBody` — the same composition the
            pre-encounter column's Note section renders, cumulative by construction (all
            questionnaire sections plus A/P, never just the active step). The organism's
            patient-stamp and reason-for-visit seams are fed pass-through; sub-fields with no wire
            value stay `undefined` and the organism omits them. */}
        <GeneratedNoteBody
          patient={patient}
          reasonForVisit={reasonForVisit}
          noteSections={noteSections}
          apNoteSections={apNoteSections}
          docs={docs}
          docsPatientId={docsPatientId}
          docsEncounterId={docsEncounterId}
          chiefComplaints={chiefComplaints}
          canStartEncounter={canStartEncounter}
          isAppointmentStarted={isAppointmentStarted}
          onGoToSection={onGoToSection}
          activeStep={activeStep}
        />
        {/* Attestation (design 836-844) — a STATEMENT of record, distinct from the action
            pills below. Rendered only when the sign-lock record carries a real timestamp. */}
        <SignedAttestation signed={signed} signedAt={signedAt} />
        {/* D4: the note footer sits UNDER the note body — the frame's terminal action row. */}
        {noteFooter}
      </PanelSection>
    </ContextRail>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A/P note body (design lines 820-835)
//
// DS-FIRST CHECK — can `GeneratedNote` already express this row? NO, and the gap is
// structural, not cosmetic:
//   • `GeneratedNoteSection.fields` is the only code-adjacent seam, and it is a
//     `{ label: string; value: ReactNode }` DEFINITION LIST rendered at
//     `grid-cols-[minmax(96px,38%)_1fr]`, 13px, proportional type, one value line.
//     The design's row is a FIXED 72px MONOSPACE code gutter + a two-line name/sub stack.
//   • `label` is typed `string`, so the mono/width treatment cannot be smuggled in as a node.
//   • The row's name colour is a per-row semantic (diagnosis = context, order = actionable);
//     `fields` renders every value in one colour.
//   • The group heading is 12px bold `--color-brand-depth` with a per-group case rule;
//     the organism's own heading is 11px uppercase `--color-text-muted`, always.
// So this block is built LOCALLY against tokens and flagged as a DS GRADUATION CANDIDATE:
// `GeneratedNote` wants a `codedRows?: { code?, name?, sub?, tone? }[]` section seam (or a
// `CodedNoteRow` molecule) so the A/P mirror stops being host-drawn. Everything the organism
// CAN express here (identity stamp, reason for visit) is still the organism's — this only
// covers the rows it cannot.
// ─────────────────────────────────────────────────────────────────────────────
